import React, { useEffect, useState } from 'react';
import { X, ChevronRight, Sparkles, Zap, FileText, ArrowRight, Gift } from 'lucide-react';
type PromotionType = 'new' | 'recommended' | 'upgrade';
interface PromotionCard {
  id: string;
  type: PromotionType;
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  icon?: React.ReactNode;
}
const MOCK_PROMOTIONS: PromotionCard[] = [{
  id: '1',
  type: 'new',
  title: 'HR Help is live',
  description: 'Talk to HR pros 24/7 and access HR templates.',
  ctaText: 'Explore HR',
  ctaLink: '/hr-help',
  icon: <Sparkles className="w-3 h-3 text-violetBlue" data-id="element-942" />
}, {
  id: '2',
  type: 'recommended',
  title: 'Document Review',
  description: 'Get expert review in 48 hours.',
  ctaText: 'Request review',
  ctaLink: '/documents/review',
  icon: <FileText className="w-3 h-3 text-turquoise" data-id="element-943" />
}, {
  id: '3',
  type: 'upgrade',
  title: 'Unlock unlimited AI',
  description: 'Upgrade for higher limits + premium features.',
  ctaText: 'Upgrade',
  ctaLink: '/upgrade',
  icon: <Zap className="w-3 h-3 text-turquoise" data-id="element-944" />
}];
export function PromotionsUpdates() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Filter out dismissed cards
  const activePromotions = MOCK_PROMOTIONS.filter(p => !dismissedIds.includes(p.id));
  const currentCard = activePromotions[currentIndex];
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);
  // Reset index if it goes out of bounds after dismissal
  useEffect(() => {
    if (currentIndex >= activePromotions.length && activePromotions.length > 0) {
      setCurrentIndex(0);
    }
  }, [activePromotions.length, currentIndex]);
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentCard) return;
    setDismissedIds(prev => [...prev, currentCard.id]);
  };
  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % activePromotions.length);
  };
  if (!isVisible || activePromotions.length === 0 && !isLoading) {
    return null;
  }
  if (isLoading) {
    return <div className="mt-auto" data-id="element-945">
        <div className="bg-white rounded-xl p-4 border border-borderColor shadow-sm animate-pulse" data-id="element-946">
          <div className="h-3 w-20 bg-aliceBlue rounded mb-3" data-id="element-947"></div>
          <div className="h-4 w-3/4 bg-aliceBlue rounded mb-2" data-id="element-948"></div>
          <div className="h-3 w-full bg-aliceBlue rounded mb-4" data-id="element-949"></div>
          <div className="h-8 w-full bg-aliceBlue rounded" data-id="element-950"></div>
        </div>
      </div>;
  }
  if (!currentCard) {
    return null;
  }
  const getBadgeColor = (type: PromotionType) => {
    switch (type) {
      case 'new':
        return 'bg-aliceBlue text-violetBlue border-violetBlue/20';
      case 'recommended':
        return 'bg-aliceBlue text-turquoise border-turquoise/20';
      case 'upgrade':
        return 'bg-aliceBlue text-turquoise border-turquoise/20';
      default:
        return 'bg-aliceBlue text-secondary border-borderColor';
    }
  };
  return <div className="mt-auto" data-id="element-951">
      <div className="bg-white rounded-xl p-4 border border-borderColor shadow-sm relative overflow-hidden group transition-all hover:border-violetBlue/30 hover:shadow-md" data-id="element-952">
        {/* Header */}
        <div className="flex justify-between items-center mb-3" data-id="element-953">
          <div className="flex items-center gap-2" data-id="element-954">
            <Gift className="w-3.5 h-3.5 text-violetBlue" data-id="element-955" />
            <h3 className="text-xs font-bold text-secondary uppercase tracking-wider" data-id="element-956">
              What's New
            </h3>
          </div>
          <button onClick={handleDismiss} className="text-disabled hover:text-secondary transition-colors p-1 -mr-1 rounded-full hover:bg-aliceBlue" aria-label="Dismiss" data-id="element-957">
            <X className="w-3.5 h-3.5" data-id="element-958" />
          </button>
        </div>

        {/* Card Content */}
        <div className="relative" data-id="element-959">
          {/* Badge */}
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border mb-2 ${getBadgeColor(currentCard.type)}`} data-id="element-960">
            {currentCard.icon}
            <span className="uppercase tracking-wide" data-id="element-961">{currentCard.type}</span>
          </div>

          <h4 className="text-sm font-bold text-primary mb-1 line-clamp-1" data-id="element-962">
            {currentCard.title}
          </h4>

          <p className="text-xs text-secondary mb-4 leading-relaxed line-clamp-2" data-id="element-963">
            {currentCard.description}
          </p>

          {/* CTA */}
          <button className="w-full flex items-center justify-center gap-2 bg-violetBlue hover:bg-[#6844E8] text-white text-xs font-semibold py-2.5 px-3 rounded-lg transition-all shadow-sm hover:shadow-md" data-id="element-964">
            {currentCard.ctaText}
            <ArrowRight className="w-3 h-3 opacity-90" data-id="element-965" />
          </button>
        </div>

        {/* Pagination / Footer */}
        {activePromotions.length > 1 && <div className="flex items-center justify-between mt-3 pt-3 border-t border-borderColor" data-id="element-966">
            <div className="flex gap-1" data-id="element-967">
              {activePromotions.map((_, idx) => <div key={idx} className={`h-1 rounded-full transition-all ${idx === currentIndex ? 'w-4 bg-violetBlue' : 'w-1 bg-disabled'}`} data-id="element-968" />)}
            </div>

            <button onClick={handleNext} className="text-[10px] text-secondary hover:text-violetBlue flex items-center gap-1 transition-colors font-medium" data-id="element-969">
              Next <ChevronRight className="w-3 h-3" data-id="element-970" />
            </button>
          </div>}
      </div>
    </div>;
}